import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AutoAwesome, Close, KeyboardArrowDown, Menu as MenuIcon } from "@mui/icons-material";
import { PAGE_GUTTER_X } from "./pageLayout";

interface NavRoute {
  label: string;
  to: string;
  /** Path prefixes (or exact paths) that mark this entry active. */
  match: (pathname: string) => boolean;
}

const PRIMARY_ROUTES: NavRoute[] = [
  { label: "RFQs", to: "/", match: (p) => p === "/" || p === "/rfqs" || p.startsWith("/rfqs/") },
  { label: "Quote History", to: "/quote-history", match: (p) => p.startsWith("/quote-history") },
  { label: "Tracking", to: "/tracking", match: (p) => p.startsWith("/tracking") },
  { label: "Analytics", to: "/analytics", match: (p) => p.startsWith("/analytics") },
];

const DATABASE_ROUTES: NavRoute[] = [
  { label: "Ink Database", to: "/databases/ink", match: (p) => p.startsWith("/databases/ink") },
  { label: "Generic Items", to: "/databases/generic", match: (p) => p.startsWith("/databases/generic") },
  { label: "Curated Prices", to: "/databases/curated", match: (p) => p.startsWith("/databases/curated") },
  { label: "General items", to: "/databases/general", match: (p) => p.startsWith("/databases/general") },
];

const BLACKLIST_ROUTE: NavRoute = {
  label: "Blacklist",
  to: "/blacklist",
  match: (p) => p.startsWith("/blacklist"),
};

const isDatabasesActive = (pathname: string) => pathname.startsWith("/databases");

/** The AI Analyst lives in ChatPanel's floating button; the nav entry just opens it. */
const openAiAnalyst = () => {
  const fab = document.querySelector('[aria-label="AI Analyst"]');
  if (fab) (fab as HTMLElement).click();
};

const navButtonSx = (active: boolean) => ({
  color: active ? "common.white" : alpha("#fff", 0.86),
  fontWeight: active ? 600 : 500,
  px: 1.5,
  py: 0.75,
  minWidth: 0,
  whiteSpace: "nowrap",
  bgcolor: active ? alpha("#fff", 0.18) : "transparent",
  "&:hover": { bgcolor: active ? alpha("#fff", 0.24) : alpha("#fff", 0.1) },
  "&.Mui-focusVisible": { outline: `2px solid ${alpha("#fff", 0.9)}`, outlineOffset: 1 },
});

export default function NavBar() {
  const { pathname } = useLocation();
  const [databaseMenuAnchor, setDatabaseMenuAnchor] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const databaseMenuOpen = Boolean(databaseMenuAnchor);

  const handleDatabaseMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDatabaseMenuAnchor(event.currentTarget);
  };

  const handleDatabaseMenuClose = () => {
    setDatabaseMenuAnchor(null);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const renderNavButton = (route: NavRoute) => {
    const active = route.match(pathname);
    return (
      <Button
        key={route.to}
        component={Link}
        to={route.to}
        aria-current={active ? "page" : undefined}
        sx={navButtonSx(active)}
      >
        {route.label}
      </Button>
    );
  };

  const renderDrawerItem = (route: NavRoute, nested = false) => {
    const active = route.match(pathname);
    return (
      <ListItemButton
        key={route.to}
        component={Link}
        to={route.to}
        selected={active}
        aria-current={active ? "page" : undefined}
        onClick={closeDrawer}
        sx={{ borderRadius: 1, mx: 1, pl: nested ? 3 : 2 }}
      >
        <ListItemText
          primary={route.label}
          primaryTypographyProps={{ variant: "body2", fontWeight: active ? 600 : 500 }}
        />
      </ListItemButton>
    );
  };

  return (
    <AppBar position="static">
      <Toolbar sx={{ minHeight: { xs: 56 }, px: PAGE_GUTTER_X, gap: 2 }} disableGutters>
        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, fontWeight: 600, fontSize: "1.125rem", whiteSpace: "nowrap" }}
        >
          DBR RFQ System
        </Typography>

        {/* lg and up: one row of links */}
        <Box component="nav" aria-label="Main" sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 0.5 }}>
          {PRIMARY_ROUTES.map((route) => renderNavButton(route))}
          <Button
            onClick={handleDatabaseMenuOpen}
            endIcon={<KeyboardArrowDown />}
            aria-haspopup="menu"
            aria-expanded={databaseMenuOpen ? "true" : undefined}
            sx={{ ...navButtonSx(isDatabasesActive(pathname)), "& .MuiButton-endIcon": { ml: 0.25 } }}
          >
            Databases
          </Button>
          <Menu
            anchorEl={databaseMenuAnchor}
            open={databaseMenuOpen}
            onClose={handleDatabaseMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            {DATABASE_ROUTES.map((route) => (
              <MenuItem
                key={route.to}
                component={Link}
                to={route.to}
                selected={route.match(pathname)}
                onClick={handleDatabaseMenuClose}
              >
                {route.label}
              </MenuItem>
            ))}
          </Menu>
          {renderNavButton(BLACKLIST_ROUTE)}
          <Button
            onClick={openAiAnalyst}
            startIcon={<AutoAwesome fontSize="small" />}
            sx={{
              ...navButtonSx(false),
              ml: 1,
              border: `1px solid ${alpha("#fff", 0.45)}`,
              "& .MuiButton-startIcon": { mr: 0.75 },
            }}
          >
            AI Analyst
          </Button>
        </Box>

        {/* below lg: menu button + drawer - with seven entries the bar does not fit at 900-1200px */}
        <IconButton
          color="inherit"
          edge="end"
          aria-label="Open navigation menu"
          onClick={() => setDrawerOpen(true)}
          sx={{ display: { xs: "inline-flex", lg: "none" } }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        sx={{ display: { lg: "none" } }}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, py: 1.5 }}>
          <Typography variant="subtitle1">DBR RFQ System</Typography>
          <IconButton aria-label="Close navigation menu" onClick={closeDrawer} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <List component="nav" aria-label="Main" sx={{ py: 1 }}>
          {PRIMARY_ROUTES.map((route) => renderDrawerItem(route))}
          <ListSubheader disableSticky sx={{ lineHeight: "32px", mt: 1, typography: "overline", color: "text.secondary" }}>
            Databases
          </ListSubheader>
          {DATABASE_ROUTES.map((route) => renderDrawerItem(route, true))}
          <Divider sx={{ my: 1 }} />
          {renderDrawerItem(BLACKLIST_ROUTE)}
          <ListItemButton
            onClick={() => {
              closeDrawer();
              openAiAnalyst();
            }}
            sx={{ borderRadius: 1, mx: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <AutoAwesome fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primary="AI Analyst" primaryTypographyProps={{ variant: "body2", fontWeight: 500 }} />
          </ListItemButton>
        </List>
      </Drawer>
    </AppBar>
  );
}
